/**
 * AccountRecoveryComponent - Account Recovery for Phone-Only Users
 *
 * This component allows users who:
 * - Have only phone authentication
 * - Lost access to their phone number
 * - Are logged out
 *
 * To recover their account using their registered email:
 * 1. User enters their email
 * 2. System finds their profile in Firestore
 * 3. System sends a magic link/password reset email
 * 4. User can then log in with email and update their phone number
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { safeInternalReturnUrl } from '../../../core/utils/return-url.util';
import { Firestore, collection, query, where, limit, getDocs, doc, updateDoc } from '@angular/fire/firestore';
import { Auth, sendPasswordResetEmail, sendSignInLinkToEmail } from '@angular/fire/auth';
import { UserProfile } from '../../../core/models/user-profile';

type RecoveryStep = 'email_input' | 'email_sent' | 'error';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';

@Component({
    selector: 'app-account-recovery',
    templateUrl: './account-recovery.component.html',
    styleUrls: ['./account-recovery.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, SharedModule]
})
export class AccountRecoveryComponent implements OnInit {
  emailForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  currentStep: RecoveryStep = 'email_input';
  foundProfile: UserProfile | null = null;
  maskedPhone = '';

  private returnUrl: string = '/';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private firestore: Firestore,
    private auth: Auth
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.captureQueryParams();
  }

  private initializeForm(): void {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  private captureQueryParams(): void {
    this.returnUrl = safeInternalReturnUrl(this.route.snapshot.queryParams['returnUrl'], '/');
  }

  /**
   * Step 1: Search for user profile by email
   */
  async onSearchByEmail(): Promise<void> {
    if (this.emailForm.invalid || this.isLoading) {
      this.emailForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const email = this.emailForm.get('email')?.value.trim().toLowerCase();

    try {
      // Search for profile in Firestore
      const usersRef = collection(this.firestore, 'users');
      const q = query(usersRef, where('email', '==', email), limit(1));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        this.errorMessage = 'AUTH.ACCOUNT_RECOVERY.ERRORS.EMAIL_NOT_FOUND';
        this.isLoading = false;
        return;
      }

      const profile = snapshot.docs[0].data() as UserProfile;
      this.foundProfile = profile;

      // Check if this user has only phone provider (needs recovery)
      const authProviders = profile.authProviders || [];
      const hasEmailAuth = authProviders.includes('email');
      const hasGoogleAuth = authProviders.includes('google');

      if (hasEmailAuth || hasGoogleAuth) {
        // User has another way to login - redirect them
        this.errorMessage = 'AUTH.ACCOUNT_RECOVERY.ERRORS.HAS_OTHER_PROVIDER';
        this.isLoading = false;
        return;
      }

      // Mask the phone number for display
      if (profile.phoneNumber) {
        this.maskedPhone = this.maskPhoneNumber(profile.phoneNumber);
      }

      // Send password reset email (creates email auth if doesn't exist)
      await this.sendRecoveryEmail(email);

      this.currentStep = 'email_sent';
      this.successMessage = 'AUTH.ACCOUNT_RECOVERY.SUCCESS.EMAIL_SENT';

    } catch (error: any) {
      console.error('❌ [AccountRecovery] Error searching for profile:', error);
      this.errorMessage = 'AUTH.ACCOUNT_RECOVERY.ERRORS.GENERIC';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Send password reset/recovery email
   * This creates an email/password auth link for the user
   */
  private async sendRecoveryEmail(email: string): Promise<void> {
    try {
      // Configure action code settings
      const actionCodeSettings = {
        url: `${window.location.origin}/auth?recoveryEmail=${encodeURIComponent(email)}`,
        handleCodeInApp: true
      };

      // Send password reset email
      await sendPasswordResetEmail(this.auth, email, actionCodeSettings);

      console.log('✅ [AccountRecovery] Recovery email sent to:', email);

      // Update the user's profile to indicate recovery was initiated
      if (this.foundProfile) {
        await updateDoc(doc(this.firestore, 'users', this.foundProfile.uid), {
          recoveryInitiated: true,
          recoveryInitiatedAt: new Date().toISOString()
        });
      }

    } catch (error: any) {
      console.error('❌ [AccountRecovery] Error sending recovery email:', error);

      // If user doesn't exist in Firebase Auth, we need to create them
      if (error.code === 'auth/user-not-found') {
        // The user only had phone auth, so they don't have an email account yet
        // We'll use a different approach - send a sign-in link
        await this.sendSignInLink(email);
      } else {
        throw error;
      }
    }
  }

  /**
   * Send email sign-in link for users who don't have email auth yet
   */
  private async sendSignInLink(email: string): Promise<void> {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/complete-recovery?email=${encodeURIComponent(email)}`,
      handleCodeInApp: true
    };

    await sendSignInLinkToEmail(this.auth, email, actionCodeSettings);

    // Store email for when user clicks the link
    localStorage.setItem('recoveryEmail', email);

    console.log('✅ [AccountRecovery] Sign-in link sent to:', email);
  }

  /**
   * Mask phone number for privacy
   */
  private maskPhoneNumber(phone: string): string {
    if (phone.length <= 4) return '****';
    const lastFour = phone.slice(-4);
    const masked = '*'.repeat(phone.length - 4);
    return masked + lastFour;
  }

  /**
   * Resend recovery email
   */
  async onResendEmail(): Promise<void> {
    if (this.isLoading || !this.foundProfile?.email) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      await this.sendRecoveryEmail(this.foundProfile.email);
      this.successMessage = 'AUTH.ACCOUNT_RECOVERY.SUCCESS.EMAIL_RESENT';
    } catch (error) {
      this.errorMessage = 'AUTH.ACCOUNT_RECOVERY.ERRORS.RESEND_FAILED';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Go back to email input step
   */
  onBackToEmail(): void {
    this.currentStep = 'email_input';
    this.errorMessage = '';
    this.successMessage = '';
    this.foundProfile = null;
  }

  /**
   * Navigate to login page
   */
  onGoToLogin(): void {
    this.router.navigate(['/auth'], {
      queryParams: { returnUrl: this.returnUrl }
    });
  }

  // Template helpers
  get emailControl() {
    return this.emailForm.get('email');
  }

  get isEmailStep(): boolean {
    return this.currentStep === 'email_input';
  }

  get isEmailSentStep(): boolean {
    return this.currentStep === 'email_sent';
  }
}
